if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.form || !this.submitButton || !this.variantIdInput) return;

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtonText = this.submitButton.querySelector('.main-atc__label__text') || this.submitButton.querySelector('span');
        this.directCheckoutRequested = false;
        this.directCheckoutButton = null;
        this.activeActionButton = null;
        this.submitting = false;
        this.directCheckoutButtons = Array.from(this.querySelectorAll('[data-skip-cart-button="true"]'));
        this.actionButtons = [this.submitButton, ...this.directCheckoutButtons];
        this.acceleratedCheckoutUpsells = this.querySelector('[data-accelerated-checkout-upsells]');
        this.productInfo = this.closest('product-info');

        this.directCheckoutButtons.forEach((button) => {
          button.addEventListener('click', () => this.onDirectCheckout(button));
        });

        if (this.acceleratedCheckoutUpsells && this.productInfo && !this.closest('product-info-upsell')) {
          this.onUpsellChange = () => this.syncAcceleratedCheckoutUpsells();
          this.onUpsellInputChange = (event) => {
            if (!event.target.closest('product-info-upsell')) return;
            queueMicrotask(() => this.syncAcceleratedCheckoutUpsells());
          };
          this.productInfo.addEventListener('upsell:change', this.onUpsellChange);
          this.productInfo.addEventListener('change', this.onUpsellInputChange);
          this.acceleratedCheckoutObserver = new MutationObserver((mutations) => {
            const addedUpsell = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) =>
              node.nodeType === Node.ELEMENT_NODE
                && (node.matches?.('product-info-upsell') || node.querySelector?.('product-info-upsell'))
            ));
            if (addedUpsell) this.syncAcceleratedCheckoutUpsells();
          });
          this.acceleratedCheckoutObserver.observe(this.productInfo, { childList: true, subtree: true });
          this.syncAcceleratedCheckoutUpsells();
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        const actionButton = this.directCheckoutRequested && this.directCheckoutButton
          ? this.directCheckoutButton
          : evt.submitter || this.submitButton;
        if (
          this.submitting
          || actionButton.disabled
          || actionButton.getAttribute('aria-disabled') === 'true'
        ) return;

        this.submitting = true;

        // A theme-editor setting change can leave the cached presenter stale.
        // Resolve the live cart surface before requesting rendered sections.
        this.cart = document.querySelector('cart-drawer')
          || document.querySelector('cart-notification')
          || this.cart;

        this.handleErrorMessage();

        this.activeActionButton = actionButton;
        this.setActionButtonsBusy(actionButton, true);

        const shouldSkipCart = this.directCheckoutRequested || this.variantIdInput?.dataset.skipCart === 'true';
        let linesUpdateDeferred = null;

        try {
          // A cart-drawer upsell is written by its own queued cart mutation. It is
          // outside this product form, so direct checkout must wait for and verify
          // that mutation before creating the checkout request.
          if (shouldSkipCart) await this.prepareDirectCheckout();

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          this.syncSelectedVariantInput();
          const formData = new FormData(this.form);
          this.removeAcceleratedCheckoutFields(formData);
          this.appendSelectedProductUpsells(formData);
          const bundleVariantIds = formData.getAll('items[][id]').filter(Boolean);
          if (bundleVariantIds.length) {
            formData.delete('id');
            formData.delete('quantity');
          }
          if (this.cart?.getSectionsToRender) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement?.(document.activeElement);
          }
          const cartRequest = this.buildMultiItemCartRequest(formData, bundleVariantIds);
          if (cartRequest) {
            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify(cartRequest);
          } else {
            config.body = formData;
          }

          const variantId = formData.get('id') || bundleVariantIds[0] || '';
          const quantity = parseInt(formData.get('quantity')) || 1;
          linesUpdateDeferred = bundleVariantIds.length ? null : this.createCartLinesUpdateEvent(variantId, quantity);

          const response = await fetch(`${routes.cart_add_url}`, config).then((result) => result.json());
            if (response.status) {
              if (
                shouldSkipCart &&
                Number(response.status) === 422 &&
                await this.canCheckoutExistingCart(formData, bundleVariantIds)
              ) {
                this.resolveCartLinesUpdate(linesUpdateDeferred);
                this.renderDirectCheckoutCart(response);
                await this.prepareDirectCheckout();
                this.submitGuestCheckout();
                return;
              }

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (shouldSkipCart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              this.renderDirectCheckoutCart(response);
              // The main product now exists, so a preselected cart upsell can
              // establish its parent_line_key before checkout is submitted.
              await this.prepareDirectCheckout();
              this.submitGuestCheckout();
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = window.CartPerformance?.createStartingMarker?.('add:wait-for-subscribers');
            if (!this.error) {
              try {
                Promise.resolve(
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: variantId,
                    cartData: response,
                  })
                )
                  .then(() => {
                    window.CartPerformance?.measureFromMarker?.('add:wait-for-subscribers', startMarker);
                  })
                  .catch((eventError) => console.error(eventError));
              } catch (eventError) {
                console.error(eventError);
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.measure("add:paint-updated-sections", () => {
                      this.renderPurchaseCart(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.measure("add:paint-updated-sections", () => {
                this.renderPurchaseCart(response);
              });
            }
        } catch (e) {
          console.error(e);
          this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
          linesUpdateDeferred?.reject(e);
        } finally {
          const completedActionButton = this.activeActionButton || this.submitButton;
          this.submitting = false;
          this.directCheckoutRequested = false;
          this.directCheckoutButton = null;
          this.activeActionButton = null;
          this.setActionButtonsBusy(completedActionButton, false);
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

          window.CartPerformance?.measureFromEvent?.("add:user-action", evt);
        }
      }

      onDirectCheckout(button) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        this.directCheckoutRequested = true;
        this.directCheckoutButton = button;
        this.form.requestSubmit(this.submitButton);
      }

      handleSubmit(event) {
        const button = event?.currentTarget || event?.target;
        event?.preventDefault?.();
        if (button?.matches?.('[data-skip-cart-button="true"]')) {
          this.onDirectCheckout(button);
          return;
        }
        this.form?.requestSubmit?.(this.submitButton);
      }

      renderDirectCheckoutCart(response) {
        // The cart notification opens itself when rendered. Direct checkout must
        // update only the drawer markup without opening either overlay.
        const drawer = document.querySelector('cart-drawer');
        if (!drawer?.renderContents || !response?.sections?.['cart-drawer']) return;
        try {
          drawer.renderContents(response, { open: false, reconcile: false });
        } catch (error) {
          // The drawer is only a preview during direct checkout. A missing
          // section must never block the checkout request or open a blank panel.
          console.error(error);
        }
      }

      renderPurchaseCart(response) {
        const presenter = document.querySelector('cart-drawer')
          || document.querySelector('cart-notification')
          || this.cart;
        this.cart = presenter;

        try {
          if (!presenter?.renderContents) throw new Error('Cart presenter is unavailable');
          const sections = presenter.getSectionsToRender?.() || [];
          const missingSection = sections.some((section) => {
            const html = response?.sections?.[section.id];
            return typeof html !== 'string' || !html.trim();
          });
          if (!response?.sections || missingSection) {
            throw new Error('Cart sections could not be rendered');
          }

          if (presenter.matches?.('cart-drawer')) {
            const source = new DOMParser()
              .parseFromString(response.sections['cart-drawer'], 'text/html')
              .querySelector('#CartDrawer');
            const target = presenter.querySelector('#CartDrawer');
            if (!source || !target || !source.querySelector('.drawer__inner')) {
              throw new Error('Cart drawer section could not be rendered');
            }
          }

          const result = presenter.renderContents(response);
          if (result === false) throw new Error('Cart sections could not be rendered');
          return true;
        } catch (error) {
          console.error(error);
          // The add succeeded, but the drawer response was unusable. The
          // server-rendered cart page is the safe, non-blank recovery surface.
          window.location.assign(window.routes?.cart_url || '/cart');
          return false;
        }
      }

      async prepareDirectCheckout() {
        if (this.closest('product-info-upsell, cart-drawer-upsell')) return;
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;

        await drawer.waitForCartMutations?.();
        for (const gift of drawer.querySelectorAll('cart-drawer-gift[data-id]')) {
          const result = await gift.update?.();
          if (result?.error) throw result.error;
        }
        if (drawer.querySelector('cart-drawer-upsell[data-toggle="true"][data-id]')) {
          if (typeof drawer.syncPreselectedUpsells !== 'function') {
            throw new Error('Cart add-ons are not ready');
          }
          const result = await drawer.syncPreselectedUpsells();
          if (result?.error) throw result.error;
          if (!Array.isArray(result?.cart?.items)) {
            throw new Error('Cart add-ons could not be verified');
          }
        }
        await drawer.waitForCartMutations?.();
      }

      submitGuestCheckout() {
        const cartUrl = window.routes?.cart_url || (typeof routes !== 'undefined' ? routes.cart_url : '/cart');
        const checkoutForm = document.createElement('form');
        checkoutForm.method = 'post';
        checkoutForm.action = cartUrl;
        checkoutForm.hidden = true;

        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'Checkout';
        checkoutForm.appendChild(checkoutInput);
        document.body.appendChild(checkoutForm);
        checkoutForm.submit();
      }

      syncSelectedVariantInput() {
        const submittedVariantId = this.form.dataset.submitVariantId;
        if (submittedVariantId && this.variantIdInput) {
          this.variantIdInput.value = submittedVariantId;
          delete this.form.dataset.submitVariantId;
          return;
        }

        const productInfo = this.closest('product-info');
        const variantPicker = productInfo?.querySelector('variant-selects');
        const selectedVariant = variantPicker?.getSelectedVariant?.();
        if (!selectedVariant?.id || !this.variantIdInput) return;

        this.variantIdInput.value = String(selectedVariant.id);
      }

      syncAcceleratedCheckoutUpsells() {
        if (!this.acceleratedCheckoutUpsells || !this.productInfo) return;

        this.syncSelectedVariantInput();
        this.acceleratedCheckoutUpsells.replaceChildren();
        const mainVariantId = String(this.variantIdInput?.value || '');
        const submittedVariantIds = new Set(mainVariantId ? [mainVariantId] : []);
        const selectedUpsells = this.getSelectedProductUpsells();
        let itemIndex = 1;

        selectedUpsells.forEach((upsell) => {
          const variantId = String(upsell.dataset.id);
          if (!variantId || submittedVariantIds.has(variantId)) return;
          submittedVariantIds.add(variantId);

          this.appendAcceleratedCheckoutInput(itemIndex, 'id', variantId);
          this.appendAcceleratedCheckoutInput(itemIndex, 'quantity', '1');
          const sellingPlan = upsell.querySelector('input[name="selling_plan"]:not(:disabled)')?.value;
          if (sellingPlan) this.appendAcceleratedCheckoutInput(itemIndex, 'selling_plan', sellingPlan);
          itemIndex += 1;
        });
      }

      appendAcceleratedCheckoutInput(itemIndex, attribute, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `shopify-additional-items[${itemIndex}][${attribute}]`;
        input.value = String(value);
        this.acceleratedCheckoutUpsells.appendChild(input);
      }

      removeAcceleratedCheckoutFields(formData) {
        Array.from(formData.keys()).forEach((name) => {
          if (name.startsWith('shopify-additional-items[')) formData.delete(name);
        });
      }

      buildMultiItemCartRequest(formData, bundleVariantIds) {
        if (!bundleVariantIds.length) return null;

        const items = [];
        let currentItem = null;
        const mainProperties = {};

        formData.forEach((value, name) => {
          if (name === 'items[][id]') {
            currentItem = { id: Number(value), quantity: 1 };
            items.push(currentItem);
            return;
          }
          if (name === 'items[][quantity]' && currentItem) {
            currentItem.quantity = Math.max(1, parseInt(value, 10) || 1);
            return;
          }
          if (name === 'items[][selling_plan]' && currentItem && value) {
            currentItem.selling_plan = Number(value);
            return;
          }

          const propertyMatch = name.match(/^properties\[(.+)\]$/);
          if (propertyMatch && value !== '') mainProperties[propertyMatch[1]] = String(value);
        });

        if (!items.length) return null;
        if (Object.keys(mainProperties).length) items[0].properties = mainProperties;

        const request = { items };
        const sections = formData.get('sections');
        const sectionsUrl = formData.get('sections_url');
        if (sections) request.sections = String(sections);
        if (sectionsUrl) request.sections_url = String(sectionsUrl);
        return request;
      }

      getSelectedProductUpsells() {
        if (!this.productInfo) return [];
        return Array.from(
          this.productInfo.querySelectorAll('product-info-upsell[data-selected="true"]')
        ).filter((upsell) => upsell.dataset.id);
      }

      setActionButtonsBusy(activeButton, busy) {
        this.actionButtons.forEach((button) => {
          if (busy) button.setAttribute('aria-disabled', 'true');
          else if (!this.error && !button.disabled) button.removeAttribute('aria-disabled');
        });

        activeButton?.classList.toggle('loading', busy);
        activeButton
          ?.querySelector('.loading-overlay__spinner, .loading__spinner')
          ?.classList.toggle('hidden', !busy);
      }

      async canCheckoutExistingCart(formData, bundleVariantIds) {
        const fields = Array.from(formData.entries());
        const hasSellingPlan = fields.some(([name, value]) => name.includes('selling_plan') && value);
        const hasLineProperties = fields.some(([name, value]) => name.includes('properties[') && value);
        if (hasSellingPlan || hasLineProperties) return false;

        const variantIds = bundleVariantIds.length
          ? formData.getAll('items[][id]').filter(Boolean)
          : [formData.get('id')].filter(Boolean);
        const quantities = bundleVariantIds.length
          ? formData.getAll('items[][quantity]')
          : [formData.get('quantity')];
        if (!variantIds.length) return false;

        const requestedQuantities = new Map();
        variantIds.forEach((variantId, index) => {
          const id = String(variantId);
          const quantity = Math.max(1, parseInt(quantities[index], 10) || 1);
          requestedQuantities.set(id, (requestedQuantities.get(id) || 0) + quantity);
        });

        try {
          const cartPath = window.routes?.cart_url || (typeof routes !== 'undefined' ? routes.cart_url : '/cart');
          const response = await fetch(`${cartPath.replace(/\/$/, '')}.js`, {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) return false;

          const cart = await response.json();
          const cartQuantities = new Map();
          (cart.items || []).forEach((item) => {
            const id = String(item.variant_id || item.id || '');
            if (!id) return;
            cartQuantities.set(id, (cartQuantities.get(id) || 0) + (parseInt(item.quantity, 10) || 0));
          });

          return Array.from(requestedQuantities.entries()).every(
            ([variantId, quantity]) => (cartQuantities.get(variantId) || 0) >= quantity
          );
        } catch (_error) {
          return false;
        }
      }

      appendSelectedProductUpsells(formData) {
        if (this.closest('product-info-upsell')) return;

        const selectedUpsells = this.getSelectedProductUpsells();
        if (!selectedUpsells.length) return;

        const existingVariantIds = formData.getAll('items[][id]').filter(Boolean).map(String);
        const submittedVariantIds = new Set(existingVariantIds);
        if (!existingVariantIds.length) {
          const mainVariantId = formData.get('id');
          if (!mainVariantId) return;

          const mainQuantity = formData.get('quantity') || '1';
          const mainSellingPlan = formData.get('selling_plan');
          submittedVariantIds.add(String(mainVariantId));
          formData.delete('id');
          formData.delete('quantity');
          formData.delete('selling_plan');
          formData.append('items[][id]', mainVariantId);
          formData.append('items[][quantity]', mainQuantity);
          if (mainSellingPlan) formData.append('items[][selling_plan]', mainSellingPlan);
        }

        selectedUpsells.forEach((upsell) => {
          const variantId = String(upsell.dataset.id);
          if (submittedVariantIds.has(variantId)) return;
          submittedVariantIds.add(variantId);
          formData.append('items[][id]', variantId);
          formData.append('items[][quantity]', '1');
          const sellingPlan = upsell.querySelector('input[name="selling_plan"]:not(:disabled)')?.value;
          if (sellingPlan) formData.append('items[][selling_plan]', sellingPlan);
        });
      }

      disconnectedCallback() {
        this.acceleratedCheckoutObserver?.disconnect();
        this.productInfo?.removeEventListener('upsell:change', this.onUpsellChange);
        this.productInfo?.removeEventListener('change', this.onUpsellInputChange);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = this.submitButton.dataset.customLabel || window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      measure(name, callback) {
        if (window.CartPerformance?.measure) window.CartPerformance.measure(name, callback);
        else callback();
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

    }
  );
}
